import { useEffect, useRef, useState } from 'react';
import FlowSheetSection from './FlowSheetSection';
import FlowSheetModal, {
  FlowSheetFormField,
  FlowSheetOutlineButton,
  FlowSheetSelectField,
  FlowSheetSubmitButton,
  FlowSheetVolumeField,
} from './FlowSheetModal';
import { FLUID_INPUT_TYPES, FLUID_OUTPUT_TYPES, nowTimeString } from '../../constants/flowSheet';
import {
  calculateFluidBalanceForCalendarDay,
  composeDateTimeFromClockTime,
} from '../../utils/flowSheetData';

function FluidRow({ row, fields }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-700">
      {row.source === 'voice' && (
        <span className="mb-1 inline-block rounded-pill bg-primary-light px-2 py-0.5 text-[10px] font-semibold text-primary">
          Voice
        </span>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {fields.map(([label, value]) => (
          <span key={label}>
            <span className="font-semibold text-gray-500">{label}:</span> {value || '—'}
          </span>
        ))}
      </div>
    </div>
  );
}

function BalanceSummary({ balance, balanceConcern }) {
  const netClass =
    balanceConcern === 'concerning'
      ? 'text-[#DC2626]'
      : balanceConcern === 'monitor'
        ? 'text-[#D97706]'
        : balanceConcern === 'normal'
          ? 'text-success'
          : 'text-gray-900';

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        24-hour balance · {balance.dayLabel}
      </p>
      <div className="flex justify-between">
        <span className="text-gray-600">Total Input</span>
        <span className="font-semibold">{balance.totalInput} ml</span>
      </div>
      <div className="mt-1 flex justify-between">
        <span className="text-gray-600">Total Output</span>
        <span className="font-semibold">{balance.totalOutput} ml</span>
      </div>
      <div className="mt-2 flex justify-between border-t border-gray-100 pt-2">
        <span className="font-semibold text-gray-800">Net Balance</span>
        <span className={`font-bold ${netClass}`}>
          {balance.net >= 0 ? '+' : ''}
          {balance.net} ml
        </span>
      </div>
    </div>
  );
}

export default function FlowSheetFluidSection({
  patientId,
  flowSheetData,
  mergedFluids,
  balanceConcern,
  fluidTask,
  addFluidInput,
  addFluidOutput,
  onMarkFluidComplete,
  showSectionHeader = true,
}) {
  const volumeInputRef = useRef(null);
  const volumeOutputRef = useRef(null);
  const [inputOpen, setInputOpen] = useState(false);
  const [outputOpen, setOutputOpen] = useState(false);
  const [inputForm, setInputForm] = useState({
    time: nowTimeString(),
    type: FLUID_INPUT_TYPES[0],
    typeOther: '',
    volume: '',
  });
  const [outputForm, setOutputForm] = useState({
    time: nowTimeString(),
    type: FLUID_OUTPUT_TYPES[0],
    typeOther: '',
    volume: '',
  });

  const fluidInputs = mergedFluids?.fluidInputs || flowSheetData.fluidInputs || [];
  const fluidOutputs = mergedFluids?.fluidOutputs || flowSheetData.fluidOutputs || [];
  const balance = calculateFluidBalanceForCalendarDay(fluidInputs, fluidOutputs, new Date());
  const entryCount = fluidInputs.length + fluidOutputs.length;

  useEffect(() => {
    if (!inputOpen) return;
    const timer = setTimeout(() => volumeInputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [inputOpen]);

  useEffect(() => {
    if (!outputOpen) return;
    const timer = setTimeout(() => volumeOutputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [outputOpen]);

  const resetInputForm = () => ({
    time: nowTimeString(),
    type: FLUID_INPUT_TYPES[0],
    typeOther: '',
    volume: '',
  });

  const resetOutputForm = () => ({
    time: nowTimeString(),
    type: FLUID_OUTPUT_TYPES[0],
    typeOther: '',
    volume: '',
  });

  const saveInput = () => {
    if (!inputForm.volume) return;
    const datetime = composeDateTimeFromClockTime(inputForm.time);
    addFluidInput(patientId, {
      time: inputForm.time,
      datetime,
      type: inputForm.type,
      typeOther: inputForm.typeOther,
      volumeInfused: inputForm.volume,
    });
    setInputOpen(false);
    setInputForm(resetInputForm());
  };

  const saveOutput = () => {
    if (!outputForm.volume) return;
    const datetime = composeDateTimeFromClockTime(outputForm.time);
    addFluidOutput(patientId, {
      time: outputForm.time,
      datetime,
      type: outputForm.type,
      typeOther: outputForm.typeOther,
      volume: outputForm.volume,
    });
    setOutputOpen(false);
    setOutputForm(resetOutputForm());
  };

  const openInputModal = () => {
    setInputForm(resetInputForm());
    setInputOpen(true);
  };

  const openOutputModal = () => {
    setOutputForm(resetOutputForm());
    setOutputOpen(true);
  };

  const content = (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <FlowSheetOutlineButton onClick={openInputModal}>+ Input</FlowSheetOutlineButton>
            <FlowSheetOutlineButton onClick={openOutputModal}>+ Output</FlowSheetOutlineButton>
          </div>
          {fluidInputs.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Input</h4>
              <div className="space-y-2">
                {fluidInputs.map((row) => (
                  <FluidRow
                    key={row.id}
                    row={row}
                    fields={[
                      ['Time', row.time],
                      ['Type', row.type === 'Other' ? row.typeOther : row.type],
                      ['Volume', row.volumeInfused ? `${row.volumeInfused} ml` : '—'],
                    ]}
                  />
                ))}
              </div>
            </div>
          )}

          {fluidOutputs.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Output</h4>
              <div className="space-y-2">
                {fluidOutputs.map((row) => (
                  <FluidRow
                    key={row.id}
                    row={row}
                    fields={[
                      ['Time', row.time],
                      ['Type', row.type === 'Other' ? row.typeOther : row.type],
                      ['Volume', row.volume ? `${row.volume} ml` : '—'],
                    ]}
                  />
                ))}
              </div>
            </div>
          )}

          <BalanceSummary balance={balance} balanceConcern={balanceConcern} />
          {entryCount > 0 && fluidTask?.status !== 'complete' && (
            <div className="pt-1">
              <button
                type="button"
                onClick={onMarkFluidComplete}
                className="w-full rounded-xl border-2 border-primary py-2.5 text-sm font-semibold text-primary"
              >
                Mark Fluid Balance Complete
              </button>
            </div>
          )}
        </div>
  );

  return (
    <>
      {showSectionHeader ? (
        <FlowSheetSection sectionKey="fluids">{content}</FlowSheetSection>
      ) : (
        <div className="card-surface p-3">{content}</div>
      )}

      <FlowSheetModal title="Add Fluid Input" open={inputOpen} onClose={() => setInputOpen(false)}>
        <div className="space-y-3">
          <FlowSheetSelectField
            label="Type"
            value={inputForm.type}
            onChange={(e) => setInputForm({ ...inputForm, type: e.target.value })}
            options={FLUID_INPUT_TYPES}
          />
          {inputForm.type === 'Other' && (
            <FlowSheetFormField
              label="Other type"
              value={inputForm.typeOther}
              onChange={(e) => setInputForm({ ...inputForm, typeOther: e.target.value })}
            />
          )}
          <FlowSheetVolumeField
            label="Volume (mL)"
            inputRef={volumeInputRef}
            value={inputForm.volume}
            onChange={(e) => setInputForm({ ...inputForm, volume: e.target.value })}
          />
          <FlowSheetFormField
            label="Time"
            type="time"
            value={inputForm.time}
            onChange={(e) => setInputForm({ ...inputForm, time: e.target.value })}
          />
          <FlowSheetSubmitButton onClick={saveInput}>Save</FlowSheetSubmitButton>
        </div>
      </FlowSheetModal>

      <FlowSheetModal title="Add Output" open={outputOpen} onClose={() => setOutputOpen(false)}>
        <div className="space-y-3">
          <FlowSheetSelectField
            label="Type"
            value={outputForm.type}
            onChange={(e) => setOutputForm({ ...outputForm, type: e.target.value })}
            options={FLUID_OUTPUT_TYPES}
          />
          {outputForm.type === 'Other' && (
            <FlowSheetFormField
              label="Other type"
              value={outputForm.typeOther}
              onChange={(e) => setOutputForm({ ...outputForm, typeOther: e.target.value })}
            />
          )}
          <FlowSheetVolumeField
            label="Volume (mL)"
            inputRef={volumeOutputRef}
            value={outputForm.volume}
            onChange={(e) => setOutputForm({ ...outputForm, volume: e.target.value })}
          />
          <FlowSheetFormField
            label="Time"
            type="time"
            value={outputForm.time}
            onChange={(e) => setOutputForm({ ...outputForm, time: e.target.value })}
          />
          <FlowSheetSubmitButton onClick={saveOutput}>Save</FlowSheetSubmitButton>
        </div>
      </FlowSheetModal>
    </>
  );
}
